import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { JumpingDots } from "../../../components/UIToolkit/Animations/JumpingDots";
import { StatStrip, type StatStripItem, type StatStripVariant } from "../../../components/UIToolkit/molecules/StatStrip";
import { SystemReactTinyPopover } from "../../../components/UIToolkit/Popups/SystemReactTinyPopover";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { BADGE, FONT_SIZE, INK, MOTION, NEUTRAL, SHADOW } from "../../../theme/tokens";
import { usePbjStaffingHoursSummary } from "../hooks/usePbjAudit";
import { DstBadge } from "./DstBadge";
import { type BreakdownSplit, StaffingHoursJobsBreakdown } from "./StaffingHoursJobsBreakdown";

type Props = {
	startDate: string;
	endDate: string;
	/** `band` (default) is the full-width panel above a grid; `inline` is the figure run for a toolbar row. */
	variant?: StatStripVariant;
};

/**
 * Viewport width at which the sub-tab row can carry every figure beside the sub-tab pills. Derived
 * from those widths, not a design breakpoint, so it lives here.
 */
const FULL_RUN_MIN_VIEWPORT_PX = 1400;

/** Compact trigger height so the button nests inside the StatStrip pill without inflating it. */
const BY_JOB_TRIGGER_HEIGHT_PX = 34;

/**
 * Group-by summary of the Staffing Hours grid — total / weekend / weekday / agency figures, with the
 * per-PBJ-job totals tucked into a breakdown popover on the trailing segment. Backend-computed from the
 * very rows the grid renders, so the figures always reconcile.
 *
 * `inline` rides the sub-tab row beside the Staffing Hours / Employees pills, so the summary costs the table no
 * height. That row only fits the whole run on a wide viewport; narrower, the strip keeps just the total
 * (the number actually filed to CMS) and the splits move into the breakdown popover — narrowing never
 * hides a figure, it only relocates it one click away.
 */
export const StaffingHoursSummary: FC<Props> = ({ startDate, endDate, variant = "band" }) => {
	const isBand = variant === "band";
	// Sub-tab row = the pills (~280) + this strip (~720 with every figure) + gap, inside the nav rail (240)
	// and the page/card padding (~128). Below that the strip keeps only the total.
	const roomForAllFigures = useMediaQuery(`(min-width:${FULL_RUN_MIN_VIEWPORT_PX}px)`);
	const showAllFigures = isBand || roomForAllFigures;
	const { t, i18n } = useTranslation();
	const { data, isLoading, isError } = usePbjStaffingHoursSummary(startDate, endDate);
	const [jobsOpen, setJobsOpen] = useState(false);
	const closeJobs = useCallback(() => setJobsOpen(false), []);

	// Locale-grouped, always two decimals ("36,741.29") — shared by the strip and the breakdown popover.
	const hoursFormatter = useMemo(() => new Intl.NumberFormat(i18n.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 }), [i18n.language]);
	const formatHours = useCallback((hours: number) => hoursFormatter.format(hours), [hoursFormatter]);

	const statItems = useMemo<StatStripItem[]>(() => {
		if (!data) return [];
		const items: StatStripItem[] = [
			{
				// The compact run drops the redundant "hours" — every figure on it is hours, on a tab named for them.
				label: showAllFigures && !isBand ? t("pbj_model.staffing.summary.total_short") : t("pbj_model.staffing.summary.total"),
				value: formatHours(data.totalHours),
				emphasis: true,
				dataTestId: "pbj-model-staffing-summary-total",
			},
		];
		if (!showAllFigures) return items;
		items.push(
			{ label: t("pbj_model.staffing.summary.weekend"), value: formatHours(data.weekendHours), dataTestId: "pbj-model-staffing-summary-weekend" },
			{ label: t("pbj_model.staffing.summary.weekday"), value: formatHours(data.weekdayHours), dataTestId: "pbj-model-staffing-summary-weekday" },
			{ label: t("pbj_model.staffing.summary.contract"), value: formatHours(data.contractHours), dataTestId: "pbj-model-staffing-summary-contract" },
		);
		// INFORMATIONAL — only surfaced when at least one employee-day crossed a DST transition. It never
		// changes any hours figure; the tooltip explains that worked hours already reflect real elapsed time.
		if (data.dstAffectedDays > 0) {
			items.push({
				label: t("pbj_model.staffing.summary.dst_days"),
				value: (
					<Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
						{data.dstAffectedDays}
						<DstBadge dataTestId="pbj-model-staffing-summary-dst-info" />
					</Box>
				),
				dataTestId: "pbj-model-staffing-summary-dst-days",
			});
		}
		return items;
	}, [data, t, formatHours, showAllFigures, isBand]);

	// The figures the strip couldn't fit, relocated to the top of the breakdown popover.
	const popoverSplits = useMemo<BreakdownSplit[]>(() => {
		if (!data || showAllFigures) return [];
		const splits: BreakdownSplit[] = [
			{ label: t("pbj_model.staffing.summary.weekend"), value: formatHours(data.weekendHours) },
			{ label: t("pbj_model.staffing.summary.weekday"), value: formatHours(data.weekdayHours) },
			{ label: t("pbj_model.staffing.summary.contract"), value: formatHours(data.contractHours) },
		];
		if (data.dstAffectedDays > 0) splits.push({ label: t("pbj_model.staffing.summary.dst_days"), value: String(data.dstAffectedDays) });
		return splits;
	}, [data, t, formatHours, showAllFigures]);

	// Same-shape placeholder as the loaded strip, so neither variant jumps when the figures arrive.
	const placeholderSx = {
		display: isBand ? "flex" : "inline-flex",
		alignItems: "center",
		minHeight: isBand ? 64 : 32,
		px: 2,
		borderRadius: isBand ? 2 : 1,
		...(isBand ? { border: `1px solid ${NEUTRAL[200]}`, backgroundColor: NEUTRAL[50] } : {}),
	} as const;

	if (isLoading) {
		return (
			<Box sx={{ ...placeholderSx, justifyContent: "center" }}>
				<JumpingDots />
			</Box>
		);
	}
	if (isError) {
		return (
			<Box sx={placeholderSx}>
				<Typography variant="body2" sx={{ color: "error.main" }} data-testid="pbj-model-staffing-summary-error">
					{t("pbj_model.staffing.summary.failed")}
				</Typography>
			</Box>
		);
	}
	if (!data) return null;

	const byJobTrigger =
		data.jobTotals.length > 0 ? (
			<SystemReactTinyPopover
				isOpen={jobsOpen}
				onClose={closeJobs}
				positions={["bottom", "top", "left"]}
				align="end"
				content={<StaffingHoursJobsBreakdown jobTotals={data.jobTotals} formatHours={formatHours} splits={popoverSplits} />}
			>
				<SystemButton
					buttonVariant="borderless"
					size="small"
					noFullWidthMobile
					onClick={() => setJobsOpen((open) => !open)}
					aria-expanded={jobsOpen}
					aria-haspopup="dialog"
					data-testid="pbj-model-staffing-summary-by-job"
					sx={{
						height: BY_JOB_TRIGGER_HEIGHT_PX,
						px: 1.25,
						borderRadius: 1.5,
						fontSize: FONT_SIZE.SECONDARY,
						fontWeight: 600,
						color: INK.SECONDARY,
						// The strip's pill ground is NEUTRAL[50]; the borderless variant's white fill would punch a hole in it.
						backgroundColor: jobsOpen ? NEUTRAL[100] : "transparent",
						transition: `background-color ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}, transform ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}`,
						"&:active": { transform: "scale(0.97)" },
						"@media (prefers-reduced-motion: reduce)": { transition: "none", "&:active": { transform: "none" } },
					}}
				>
					<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
						{showAllFigures ? t("pbj_model.staffing.summary.by_job") : t("pbj_model.staffing.summary.breakdown")}
						<Box
							component="span"
							sx={{
								minWidth: BADGE.PILL_SIZE,
								height: BADGE.PILL_SIZE,
								px: 0.5,
								borderRadius: `${BADGE.PILL_RADIUS}px`,
								display: "inline-flex",
								alignItems: "center",
								justifyContent: "center",
								fontSize: FONT_SIZE.LABEL_XS,
								fontWeight: 700,
								backgroundColor: NEUTRAL[200],
								color: INK.SECONDARY,
							}}
						>
							{data.jobTotals.length}
						</Box>
						<KeyboardArrowDownRounded
							sx={{
								fontSize: "1.125rem",
								color: INK.MUTED,
								transition: `transform ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}`,
								transform: jobsOpen ? "rotate(180deg)" : "rotate(0deg)",
								"@media (prefers-reduced-motion: reduce)": { transition: "none" },
							}}
						/>
					</Box>
				</SystemButton>
			</SystemReactTinyPopover>
		) : undefined;

	return (
		<StatStrip
			variant={variant}
			layout={isBand ? "stacked" : "row"}
			items={statItems}
			endSlot={byJobTrigger}
			dataTestId="pbj-model-staffing-summary"
			// Both variants read as a real surface (white + soft shadow) rather than a flat gray fill; the
			// inline pill then matches the height and radius of the controls it shares a row with.
			sx={{ backgroundColor: "background.paper", borderColor: NEUTRAL[200], boxShadow: SHADOW.PANEL_SOFT }}
		/>
	);
};
